// One pool on the warmup board: is it moving, how much of today is spent,
// how yesterday's rates stand against rule 4, what the scheduler decided,
// the week's trend and who the mail went to — and the moves a person may make.
// A budget of 0 always says why (`why`, from `simple.poolZeroReason`).

import { Link } from "react-router-dom";
import { ChevronRightIcon } from "lucide-react";
import type { WarmupKnobs, WarmupPool, WarmupThresholds } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardAction, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { LimitBar } from "@/components/emc/charts/LimitBar";
import { Sparkline } from "@/components/emc/charts/Sparkline";
import { StatusPill } from "@/components/emc/StatusPill";
import { Country } from "@/components/emc/Flag";
import { poolColor } from "@/components/emc/charts/theme";
import { cn } from "@/lib/utils";
import { count } from "@/components/emc/format";
import { PoolActions } from "@/pages/warmup/actions";
import { budgetCaption, budgetLook, dayInWords } from "@/pages/warmup/model";
import { DecisionLine, IspLine, RateGrid, StoppedNotice } from "@/pages/warmup/parts";

interface Props {
  pool: WarmupPool;
  knobs: WarmupKnobs;
  thresholds: WarmupThresholds;
  /** Why the budget is 0, when it is. */
  why?: string | null;
  onChanged: () => void;
}

export function PoolCard({ pool, knobs, thresholds, why = null, onChanged }: Props) {
  const look = budgetLook(pool, why);
  const sent = pool.today.rates?.sent ?? 0;
  const cold = pool.warmup_state === "cold";
  const spark = pool.spark.map((point) => point.sent);
  const stopped = pool.is_paused || pool.watch.state === "paused";

  return (
    <Card
      id={`pool-${pool.code}`}
      data-pool={pool.code}
      className={cn("scroll-mt-20 gap-4", stopped && "ring-1 ring-destructive/45")}
    >
      <CardHeader className="has-data-[slot=card-action]:grid-cols-[auto_1fr_auto] gap-x-3">
        <span
          aria-hidden="true"
          className="row-span-2 flex size-9 items-center justify-center self-center rounded-control bg-fill-secondary text-label font-title"
          style={{ color: poolColor(pool.code) }}
        >
          {pool.code}
        </span>
        <CardTitle className="flex min-w-0 items-baseline gap-2">
          <Link to={`/warmup/${pool.code}`} className="truncate text-foreground hover:text-blue-ink hover:no-underline">
            {pool.name || pool.code}
          </Link>
          <span className="sr-only">({pool.code})</span>
        </CardTitle>
        <CardAction className="col-start-3 flex-wrap justify-end">
          <StatusPill domain="warmup" state={pool.warmup_state} />
          <StatusPill domain="poolWatch" state={pool.watch.state} />
        </CardAction>
        <CardDescription className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
          {pool.ip_count} IP · SES {pool.ses_account}
          {pool.warmup_account ? ` (a fiók napi számából: ${count(pool.budget_today)} ma)` : ""} ·{" "}
          {pool.countries.length === 0 ? (
            "nincs fiók"
          ) : (
            <span className="inline-flex flex-wrap gap-x-2 gap-y-1">
              {pool.countries.map((country) => (
                <Country key={country} code={country} className="font-medium" />
              ))}
            </span>
          )}{" "}
          · idők:{" "}
          <span className="data-mono" title={pool.timezones.join(", ")}>
            {pool.timezone}
          </span>
        </CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        <StoppedNotice pool={pool} />

        {cold ? (
          <p className="text-muted-foreground">
            A pool hideg: még nem indult, ma semmit sem küld.
            {pool.warmup_account ? ` A(z) ${pool.warmup_account} SES-fiókkal együtt indul, a fiók kártyáján.` : ""}
            {pool.can_start
              ? ` Indításkor ${count(Math.min(knobs.start_per_ip, pool.target_daily_per_ip))} / IP / nap, naponta ×${knobs.growth.toLocaleString("hu-HU")} a ${count(pool.target_daily_per_ip)} / IP célig.`
              : ""}
          </p>
        ) : (
          <div className="flex flex-col gap-1.5">
            <LimitBar used={sent} limit={pool.budget_today} label="Mai keret" tone={look.tone} />
            <p className="text-label text-muted-foreground">{budgetCaption(pool, why)}</p>
          </div>
        )}

        {!cold && (
          <RateGrid
            yesterday={pool.yesterday}
            today={pool.today}
            thresholds={thresholds}
            minSample={knobs.min_sample}
          />
        )}

        {pool.decision && (
          <div className="flex flex-col gap-1.5">
            <span className="caps-label">
              {pool.decided_today ? "Mai döntés" : `Utolsó döntés · ${dayInWords(pool.decision.day)}`}
            </span>
            <DecisionLine decision={pool.decision} />
          </div>
        )}

        {!cold && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <span className="flex items-center gap-2">
              <span className="caps-label">8 nap</span>
              <Sparkline
                values={spark}
                color={poolColor(pool.code)}
                ariaLabel="kiment levél, 8 nap"
                format={(value) => count(value)}
                className="h-8 w-28"
              />
            </span>
            <IspLine rows={pool.yesterday.by_isp} />
          </div>
        )}
      </CardContent>

      <CardFooter className="justify-between gap-3">
        <Button variant="outline" size="sm" asChild>
          <Link to={`/warmup/${pool.code}`}>
            Görbe és napló
            <ChevronRightIcon />
          </Link>
        </Button>
        <PoolActions pool={pool} knobs={knobs} onChanged={onChanged} />
      </CardFooter>
    </Card>
  );
}
